import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Il riepilogo di un passo confermato (ADR 0033): al posto delle schede, una fascia verde in testa, i numeri in breve,
 * le righe in sola lettura raggruppate e «Modifica» su ogni riga. Una riga si apre da sola nella sua scheda e, data la
 * risposta, torna riga; la riga appena cambiata si illumina un attimo e lo dice. Nessun popup né loader.
 */

/** La riga aperta e quella appena cambiata: una sola aperta per volta. */
export function useRows() {
  const [open, setOpen] = useState<string | null>(null);
  const [edited, setEdited] = useState<string | null>(null);
  return {
    open,
    edited,
    /** Apre la riga (o la chiude, se è già aperta). */
    toggle: (id: string) => {
      setEdited(null);
      setOpen((o) => (o === id ? null : id));
    },
    /** Chiude la riga; `changed` la segna come appena cambiata. */
    close: (changed = false) => {
      if (changed && open) setEdited(open);
      setOpen(null);
    },
    /** Segna una riga come appena cambiata senza aprirla. */
    mark: (id: string) => setEdited(id),
  };
}

/** La fascia del passo confermato, con il pulsante per rivederlo da capo. */
export function DoneBand({
  title,
  text = 'Confermato da te. Puoi cambiare una riga alla volta con «Modifica».',
  onReview,
}: {
  title: string;
  text?: string;
  onReview?: (() => void) | null;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3.5">
      <span
        aria-hidden
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-lg text-white"
      >
        ✓
      </span>
      <div className="min-w-[12rem] flex-1">
        <p className="font-semibold text-emerald-900">{title}</p>
        <p className="text-sm text-emerald-800/80">{text}</p>
      </div>
      {onReview && (
        <button
          className="rounded-xl border border-emerald-600/30 bg-white px-3.5 py-2 text-sm font-medium text-emerald-900 transition hover:border-emerald-600"
          onClick={onReview}
        >
          Rivedi il passo
        </button>
      )}
    </div>
  );
}

/** I numeri del passo in breve: ciò che si vede prima di leggere le righe. */
export function Glance({ tiles }: { tiles: Array<[number | string, string] | null | false> }) {
  const shown = tiles.filter((x): x is [number | string, string] => !!x);
  if (!shown.length) return null;
  return (
    <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-[repeat(auto-fit,minmax(9rem,1fr))]">
      {shown.map(([n, label]) => (
        <div key={label} className="rounded-2xl bg-white px-3.5 py-2.5 ring-1 ring-black/5">
          <p className="text-2xl font-semibold tabular-nums tracking-tight">{n}</p>
          <p className="text-xs leading-snug text-ink/55">{label}</p>
        </div>
      ))}
    </div>
  );
}

/** Un gruppo di righe con il suo titolo (una sede, «dati dell'impresa»…). */
export function RowGroup({
  title,
  sub,
  children,
}: {
  title: ReactNode;
  sub?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="mt-5">
      <div className="mb-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 px-1">
        <h3 className="text-sm font-semibold">{title}</h3>
        {sub && <span className="text-xs text-ink/45">{sub}</span>}
      </div>
      <ul className="divide-y divide-black/5 overflow-hidden rounded-2xl bg-white ring-1 ring-black/5">
        {children}
      </ul>
    </section>
  );
}

/**
 * Una riga del riepilogo: icona, contenuto e «Modifica». Aperta, mostra al suo posto la scheda (`editor`), con un
 * bordo blu a sinistra; appena cambiata, si illumina e lo dice.
 */
export function Row({
  icon,
  children,
  open = false,
  edited = false,
  onEdit,
  editor,
  editLabel = 'Modifica',
}: {
  icon?: ReactNode;
  children: ReactNode;
  open?: boolean;
  edited?: boolean;
  /** Assente: la riga si legge soltanto. */
  onEdit?: (() => void) | null;
  editor?: ReactNode;
  editLabel?: string;
}) {
  // Aprendo una riga si chiude quella aperta prima, più in alto: la pagina si accorcia e la riga scapperebbe dallo
  // schermo. Si riporta in vista con un movimento morbido, così non si perde il punto.
  const box = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (open) box.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [open]);
  if (open && editor)
    return (
      <li
        ref={box}
        className="scroll-my-4 bg-accent/[0.02] px-4 py-3.5 shadow-[inset_3px_0_0_var(--color-accent)]"
      >
        {editor}
        <p className="mt-2 text-right">
          <button
            className="text-xs text-ink/45 hover:text-accent hover:underline"
            onClick={() => onEdit?.()}
          >
            Chiudi
          </button>
        </p>
      </li>
    );
  return (
    <li className={`flex items-center gap-3 px-4 py-3 ${edited ? 'bk-flash' : ''}`}>
      {icon && (
        <span
          aria-hidden
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-paper text-lg"
        >
          {icon}
        </span>
      )}
      <div className="min-w-0 flex-1">
        {children}
        {edited && <p className="text-xs text-accent">↻ Modificato ora</p>}
      </div>
      {onEdit && (
        <button
          className="shrink-0 rounded-lg px-2 py-1.5 text-sm font-medium text-accent transition hover:bg-accent/[0.07]"
          onClick={onEdit}
        >
          {editLabel}
        </button>
      )}
    </li>
  );
}

/** Un'etichetta accanto al nome: «Operativa», «C'è», «Da verificare». */
export function Pill({
  children,
  tone = 'gray',
}: {
  children: ReactNode;
  tone?: 'green' | 'gray' | 'amber';
}) {
  const style = {
    green: 'bg-emerald-50 text-emerald-800',
    gray: 'bg-black/[0.05] text-ink/65',
    amber: 'bg-amber-50 text-amber-800',
  }[tone];
  return (
    <span
      className={`ml-1.5 inline-block rounded-full px-2 py-0.5 align-[1px] text-xs font-medium ${style}`}
    >
      {children}
    </span>
  );
}

/** Un avviso giallo: cosa cambia altrove dopo una modifica, o cosa resta da verificare. */
export function Notice({ children }: { children: ReactNode }) {
  return (
    <p className="mt-4 flex gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <span aria-hidden>↪</span>
      <span>{children}</span>
    </p>
  );
}

/** I passi che usano ciò che si è confermato qui: dicono perché conviene che sia giusto. */
export function Feeds({ steps }: { steps: string[] }) {
  if (!steps.length) return null;
  return (
    <p className="mt-5 flex flex-wrap items-center gap-1.5 text-xs text-ink/50">
      Da questo passo dipendono:
      {steps.map((s) => (
        <span
          key={s}
          className="rounded-full bg-white px-2.5 py-0.5 text-ink/75 ring-1 ring-black/5"
        >
          {s}
        </span>
      ))}
    </p>
  );
}

/** Persone in una parola: «1 persona», «3 persone». */
export const persone = (n: number) => `${n} ${n === 1 ? 'persona' : 'persone'}`;
